import { approxYear } from '../../domain/dates';
import { changesSince, deepEqual, diffRecords, mergePatch } from '../../domain/merge';
import { displayName as displayNameOf, searchTerms, similarity } from '../../domain/names';
import { canChangePortrait, canEditPersonDirectly, isAdmin, isEditor, managesPerson, personEditMode } from '../../domain/permissions';
import { canReassignCircle, circleOwner } from '../../domain/ownership';
import type { AuditEvent, ChangeRequest, CommandResult, Person, PersonAccountLink, PersonApproval, PersonName, PersonPortrait, PersonStar, PersonWithNames, ProfileManagementGrant, UUID } from '../../domain/types';
import { byId } from '../../lib/grouped';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { DuplicateCandidate, PersonApprovalState, PersonInput, PersonRepository, PersonSearchHit, UpdatePersonCommand } from '../types';
import { actorOf, copies, denied, guarded, invalid, notFound, okResult } from './actor';
import { getBlob, portraitThumbKey, putBlob } from './blobStore';
import { MockDb, NetworkError } from './MockDb';
import { recordHomeChange } from './MockPlaceRepository';
import { smallCopy, UrlCache } from './pictureUrls';

/** The side of a face's small copy: a 104px tree circle on a 2x screen, and plenty for every row that wears one. */
const PORTRAIT_THUMB_SIDE = 256;

export class MockPersonRepository implements PersonRepository {
  constructor(private db: MockDb) {}

  private withNames(p: Person): PersonWithNames {
    return this.db.personWithNames(p.id, p)!;
  }

  async list(): Promise<PersonWithNames[]> {
    await this.db.read();
    return this.db.t.persons.filter((p) => !p.archived && !p.mergedIntoId).map((p) => this.withNames(p));
  }

  async get(id: UUID): Promise<PersonWithNames | undefined> {
    await this.db.read();
    let p = this.db.t.persons.find((x) => x.id === id);
    if (p?.mergedIntoId) p = this.db.t.persons.find((x) => x.id === p!.mergedIntoId);
    return p ? this.withNames(p) : undefined;
  }

  async search(query: string, limit = 12): Promise<PersonSearchHit[]> {
    await this.db.read();
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const hits: PersonSearchHit[] = [];
    for (const p of await this.list()) {
      let best = 0;
      let reason: PersonSearchHit['reason'] = 'name';
      for (const term of searchTerms(p)) {
        const s = term.includes(q) ? 1 : term.startsWith(q) ? 0.95 : similarity(term, q);
        if (s > best) {
          best = s;
          const n = p.names.find((nm) => `${nm.given} ${nm.family}`.toLowerCase().includes(q));
          reason = n ? (n.language === 'he' ? 'hebrew' : 'english') : p.names.some((nm) => nm.nickname?.toLowerCase().includes(q)) ? 'nickname' : p.names.some((nm) => nm.previousNames?.some((pn) => pn.toLowerCase().includes(q))) ? 'previous' : 'name';
        }
      }
      if (best >= 0.45) hits.push({ person: p, score: best, reason });
    }
    return hits.sort((a, b) => b.score - a.score).slice(0, limit);
  }

  async findLikelyDuplicates(input: { names: PersonInput['names']; birthYear?: number; connectedToPersonId?: UUID }): Promise<DuplicateCandidate[]> {
    await this.db.read();
    const out: DuplicateCandidate[] = [];
    for (const p of await this.list()) {
      let score = 0;
      const reasons: string[] = [];
      for (const n of input.names) {
        for (const m of p.names) {
          const s = similarity(`${n.given} ${n.family}`, `${m.given} ${m.family}`);
          const g = similarity(n.given, m.given);
          if (s > score) score = s;
          if (g > 0.85 && similarity(n.family, m.family) > 0.6) reasons.push('name');
        }
      }
      if (score < 0.55) continue;
      const by = approxYear(p.birth);
      if (input.birthYear && by !== null) {
        const diff = Math.abs(by - input.birthYear);
        if (diff <= 2) {
          score += 0.25;
          reasons.push('birth');
        } else if (diff > 15) score -= 0.3;
      }
      if (input.connectedToPersonId) {
        const connected = this.db.t.relationships.some((r) => (r.fromPersonId === p.id && r.toPersonId === input.connectedToPersonId) || (r.toPersonId === p.id && r.fromPersonId === input.connectedToPersonId));
        if (connected) {
          score += 0.15;
          reasons.push('connected');
        }
      }
      if (score >= 0.6) out.push({ person: p, confidence: Math.min(1, score), reasons: [...new Set(reasons)] });
    }
    return out.sort((a, b) => b.confidence - a.confidence).slice(0, 5);
  }

  async add(input: PersonInput, idempotencyKey: string): Promise<CommandResult<PersonWithNames>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const existing = this.db.idem(idempotencyKey);
      if (existing) {
        const p = await this.get(existing.entityId);
        return p ? okResult(p, { noop: true }) : notFound();
      }
      if (!input.names.length || !input.names.some((n) => n.given.trim())) return invalid('person.error.nameRequired');
      const now = nowISO();
      const person: Person = {
        id: uuid(), familyId: actor.session.familyId, status: input.status, gender: input.gender, birth: input.birth, death: input.death, birthPlaceId: input.birthPlaceId,
        portraitUrl: input.portraitUrl, portraitHue: Math.floor(Math.random() * 16), reviewStatus: isEditor(actor.session.role) ? 'approved' : 'pending', lockedFields: [],
        archived: false, notes: input.notes, version: 1, createdAt: now, updatedAt: now, updatedBy: actor.session.accountId,
      };
      this.db.t.persons.push(person);
      for (const n of input.names) this.db.t.person_names.push({ ...n, id: uuid(), personId: person.id });
      this.db.rememberIdem(idempotencyKey, 'person', person.id);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: person.id, action: 'create', description: 'Person added', entityVersion: 1, approvalStatus: person.reviewStatus === 'pending' ? 'pending' : 'approved' });
      // Whoever adds a circle looks after it (domain/ownership.ts), so it is
      // never left as something anyone can change.
      this.db.t.profile_management_grants.push({
        id: uuid(), familyId: actor.session.familyId, helperAccountId: actor.session.accountId, managedPersonId: person.id,
        scope: 'full', grantedByAccountId: actor.session.accountId, status: 'active', createdAt: now,
      });
      const tables: Parameters<MockDb['commit']>[0] = ['persons', 'person_names', 'profile_management_grants', 'audit_events', 'idempotency'];
      if (person.reviewStatus === 'pending') {
        this.db.t.change_requests.push({
          id: uuid(), familyId: actor.session.familyId, kind: 'person_add', payload: { personId: person.id },
          summary: `New person “${input.names[0].given} ${input.names[0].family}” awaiting review`, requesterAccountId: actor.session.accountId, status: 'pending',
          targetEntityType: 'person', targetEntityId: person.id, createdAt: now, updatedAt: now,
        });
        tables.push('change_requests');
      }
      await this.db.commit(tables, [person.id]);
      return okResult(this.withNames(person));
    });
  }

  async update(cmd: UpdatePersonCommand): Promise<CommandResult<PersonWithNames>> {
    const db = this.db;
    // Offline: queue the operation and report it as not yet backed up (section 13.3 / 16.3).
    if (db.isOffline()) {
      try {
        const actor = actorOf(db);
        if (!db.t.pending_operations.some((op) => op.idempotencyKey === cmd.idempotencyKey)) {
          db.t.pending_operations.push({ id: uuid(), accountId: actor.session.accountId, command: 'people.update', payload: cmd as unknown as Record<string, unknown>, idempotencyKey: cmd.idempotencyKey, entityType: 'person', entityId: cmd.personId, createdAt: nowISO(), attempts: 0 });
          await db.commit(['pending_operations']);
        }
        const current = await this.get(cmd.personId);
        return current ? okResult(current, { queuedOffline: true }) : notFound();
      } catch {
        return { ok: false, kind: 'network', messageKey: 'error.offline', retryable: true };
      }
    }
    return guarded(db, async () => this.applyUpdate(cmd, false));
  }

  /** Shared by update(), approvals and restore. `force` bypasses permission and version checks (admin approval path). */
  async applyUpdate(cmd: UpdatePersonCommand, force: boolean): Promise<CommandResult<PersonWithNames>> {
    const db = this.db;
    const actor = actorOf(db);
    const person = db.t.persons.find((p) => p.id === cmd.personId);
    if (!person) return notFound();
    const existing = db.idem(cmd.idempotencyKey);
    if (existing) return okResult(this.withNames(person), { noop: true });

    // Locked circles and circles owned by someone else take suggestions, not edits.
    if (!force && personEditMode(actor, db.t.person_account_links, db.t.profile_management_grants, person) === 'suggest') {
      const cr = await this.createFieldChangeRequest(cmd, undefined);
      return { ok: false, kind: 'review_required', changeRequestId: cr.id, messageKey: 'person.sentForReview' };
    }
    // Locks (section 18.3)
    const lockedTouched = Object.keys(cmd.patch).filter((f) => person.lockedFields.includes(f));
    if (lockedTouched.length && !isAdmin(actor.session.role) && !force) {
      return { ok: false, kind: 'locked', messageKey: 'person.locked', reason: person.lockReason };
    }

    // Field-level conflict detection against the audit trail (section 16.3)
    const base = this.withNames(person) as unknown as Record<string, unknown>;
    const patchRecord = { ...cmd.patch } as Record<string, unknown>;
    if (cmd.resolutions) {
      for (const [field, choice] of Object.entries(cmd.resolutions)) if (choice === 'keep_current') delete patchRecord[field];
    }
    let merged: Record<string, unknown> = patchRecord;
    let wasMerged = false;
    if (!force && cmd.expectedVersion !== person.version) {
      const changed = changesSince(db.t.audit_events, person.id, cmd.expectedVersion);
      // Reconstruct the record the editor started from by rolling back the server-side changes.
      const editorBase: Record<string, unknown> = { ...base };
      for (const [field, ch] of changed) editorBase[field] = ch.before;
      const res = mergePatch(editorBase, base, patchRecord, changed);
      const unresolved = res.conflicts.filter((c) => cmd.resolutions?.[c.field] !== 'use_mine');
      if (unresolved.length) {
        return { ok: false, kind: 'conflict', conflicts: unresolved, current: this.withNames(person), currentVersion: person.version };
      }
      merged = { ...res.merged };
      for (const c of res.conflicts) merged[c.field] = c.yourValue;
      wasMerged = true;
    }

    const before = this.withNames(person);
    const now = nowISO();
    const { names, ...rest } = merged as Partial<Person> & { names?: PersonInput['names'] };
    let anyChange = false;
    let homeChanged = false;
    for (const [k, v] of Object.entries(rest)) {
      if (!deepEqual((person as unknown as Record<string, unknown>)[k], v)) {
        (person as unknown as Record<string, unknown>)[k] = v;
        anyChange = true;
        if (k === 'homeLocationId') homeChanged = true;
      }
    }
    // A new home is a new chapter in the person's place record (World tab).
    if (homeChanged) recordHomeChange(db, person.id, person.homeLocationId, actor.session.accountId);
    if (names) {
      const current = db.t.person_names.filter((n) => n.personId === person.id);
      const same = current.length === names.length && current.every((c, i) => deepEqual(stripIds(c), stripIds({ ...names[i], id: '', personId: '' } as PersonName)));
      if (!same) {
        db.t.person_names = db.t.person_names.filter((n) => n.personId !== person.id);
        for (const n of names) db.t.person_names.push({ ...n, id: uuid(), personId: person.id });
        anyChange = true;
      }
    }
    if (!anyChange) {
      db.rememberIdem(cmd.idempotencyKey, 'person', person.id);
      await db.commit(['idempotency']);
      return okResult(this.withNames(person), { noop: true });
    }
    person.version += 1;
    person.updatedAt = now;
    person.updatedBy = actor.session.accountId;
    const after = this.withNames(person);
    const diff = diffRecords(before as unknown as Record<string, unknown>, after as unknown as Record<string, unknown>);
    db.audit({
      actorAccountId: actor.session.accountId, onBehalfOfPersonId: cmd.onBehalfOfPersonId, entityType: 'person', entityId: person.id, action: 'update',
      before: diff.before, after: diff.after, description: `Updated ${Object.keys(diff.after).join(', ')}`, entityVersion: person.version,
    });
    db.rememberIdem(cmd.idempotencyKey, 'person', person.id);
    await db.commit(homeChanged ? ['persons', 'person_names', 'audit_events', 'idempotency', 'person_places'] : ['persons', 'person_names', 'audit_events', 'idempotency'], [person.id]);
    return okResult(after, { merged: wasMerged });
  }

  private async createFieldChangeRequest(cmd: UpdatePersonCommand & { reason?: string }, reason: string | undefined): Promise<ChangeRequest> {
    const actor = actorOf(this.db);
    const person = this.db.personWithNames(cmd.personId);
    const now = nowISO();
    const fields = Object.keys(cmd.patch);
    const cr: ChangeRequest = {
      id: uuid(), familyId: actor.session.familyId, kind: 'person_field',
      payload: { personId: cmd.personId, patch: cmd.patch, expectedVersion: cmd.expectedVersion, idempotencyKey: cmd.idempotencyKey },
      summary: `Change ${fields.join(', ')} for ${person?.names[0]?.given ?? ''} ${person?.names[0]?.family ?? ''}`.trim(),
      requesterAccountId: actor.session.accountId, onBehalfOfPersonId: cmd.onBehalfOfPersonId, reason: reason ?? cmd.reason, status: 'pending',
      targetEntityType: 'person', targetEntityId: cmd.personId, createdAt: now, updatedAt: now,
    };
    this.db.t.change_requests.push(cr);
    this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'change_request', entityId: cr.id, action: 'create', description: cr.summary, approvalStatus: 'pending' });
    for (const m of this.db.t.family_memberships) {
      if (m.status === 'active' && (m.role === 'editor' || m.role === 'admin') && m.accountId !== actor.session.accountId) {
        this.db.notify({ accountId: m.accountId, kind: 'review', title: cr.summary, titleHe: cr.summary, link: '/manage/review' });
      }
    }
    await this.db.commit(['change_requests', 'audit_events', 'notifications']);
    return cr;
  }

  async proposeFieldChange(cmd: UpdatePersonCommand & { reason?: string }): Promise<CommandResult<ChangeRequest>> {
    return guarded(this.db, async () => okResult(await this.createFieldChangeRequest(cmd, cmd.reason)));
  }

  async claim(personId: UUID): Promise<CommandResult<{ status: 'active' | 'pending' }>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const now = nowISO();
      const taken = this.db.t.person_account_links.find((l) => l.personId === personId && l.status === 'active');
      if (taken && taken.accountId === actor.session.accountId) return okResult({ status: 'active' as const }, { noop: true });
      const status: 'active' | 'pending' = taken ? 'pending' : 'active';
      this.db.t.person_account_links = this.db.t.person_account_links.filter((l) => !(l.accountId === actor.session.accountId && l.status !== 'active'));
      this.db.t.person_account_links.push({ id: uuid(), accountId: actor.session.accountId, personId, kind: 'self', status, createdAt: now });
      if (status === 'pending') {
        this.db.t.change_requests.push({ id: uuid(), familyId: actor.session.familyId, kind: 'claim', payload: { accountId: actor.session.accountId, personId }, summary: `${this.db.accountName(actor.session.accountId)} claims a person already linked to another account`, requesterAccountId: actor.session.accountId, status: 'pending', targetEntityType: 'person', targetEntityId: personId, createdAt: now, updatedAt: now });
      }
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'account', entityId: actor.session.accountId, action: 'claim', description: status === 'active' ? 'Claimed person as self' : 'Claim submitted for review', approvalStatus: status === 'active' ? 'approved' : 'pending' });
      await this.db.commit(['person_account_links', 'change_requests', 'audit_events']);
      if (status === 'active') {
        const s = this.db.getSession();
        if (s) this.db.setSession({ ...s, personId });
      }
      return okResult({ status });
    });
  }

  // ---- photos on circles --------------------------------------------------

  /*
   * Remembered, but not for ever — see UrlCache. A face at full size is only
   * wanted a few at a time (a profile, a circle zoomed right in); small ones are
   * wanted by the hundred.
   */
  private portraitUrls = new UrlCache(120);
  private portraitThumbUrls = new UrlCache(1500);

  async addPortrait(personId: UUID, image: Blob, meta: { width: number; height: number }, idempotencyKey: string): Promise<CommandResult<PersonPortrait>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const person = this.db.t.persons.find((p) => p.id === personId);
      if (!person) return notFound();
      const existing = this.db.idem(idempotencyKey);
      if (existing) {
        const prior = this.db.t.person_portraits.find((x) => x.id === existing.entityId);
        if (prior) return okResult(prior, { noop: true });
      }
      if (!canChangePortrait(actor, this.db.t.person_account_links, this.db.t.profile_management_grants, person)) return denied('portrait.ownedByOther');
      const now = nowISO();
      const id = uuid();
      const storageKey = `portrait/${id}`;
      await putBlob(storageKey, image);
      for (const p of this.db.t.person_portraits) if (p.personId === personId) p.current = false;
      const portrait: PersonPortrait = {
        id, familyId: person.familyId, personId, storageKey, mimeType: image.type || 'image/jpeg', bytes: image.size,
        width: meta.width, height: meta.height, uploadedByAccountId: actor.session.accountId, createdAt: now, current: true,
      };
      this.db.t.person_portraits.push(portrait);
      const before = { portraitUrl: person.portraitUrl };
      person.portraitUrl = `portrait:${id}`;
      person.version += 1;
      person.updatedAt = now;
      person.updatedBy = actor.session.accountId;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: personId, action: 'update', before, after: { portraitUrl: person.portraitUrl }, description: 'Added a photo', entityVersion: person.version });
      this.db.rememberIdem(idempotencyKey, 'portrait', id);
      await this.db.commit(['person_portraits', 'persons', 'audit_events', 'idempotency'], [personId]);
      // Made now, by the device that has the picture in hand, rather than by every tree that draws it (not waited for).
      void smallCopy(portraitThumbKey(id), storageKey, PORTRAIT_THUMB_SIDE);
      return okResult(portrait);
    });
  }

  async choosePortrait(personId: UUID, portraitId: UUID): Promise<CommandResult<PersonPortrait>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const person = this.db.t.persons.find((p) => p.id === personId);
      const portrait = this.db.t.person_portraits.find((x) => x.id === portraitId && x.personId === personId);
      if (!person || !portrait) return notFound();
      if (!canChangePortrait(actor, this.db.t.person_account_links, this.db.t.profile_management_grants, person)) return denied('portrait.ownedByOther');
      if (portrait.current) return okResult(portrait, { noop: true });
      for (const p of this.db.t.person_portraits) if (p.personId === personId) p.current = p.id === portraitId;
      const now = nowISO();
      const before = { portraitUrl: person.portraitUrl };
      person.portraitUrl = `portrait:${portraitId}`;
      person.version += 1;
      person.updatedAt = now;
      person.updatedBy = actor.session.accountId;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: personId, action: 'update', before, after: { portraitUrl: person.portraitUrl }, description: 'Chose a different photo', entityVersion: person.version });
      await this.db.commit(['person_portraits', 'persons', 'audit_events'], [personId]);
      return okResult(portrait);
    });
  }

  async listPortraits(personId: UUID): Promise<PersonPortrait[]> {
    await this.db.read();
    // A photo an administrator took down is kept in storage (the decision log
    // shows it) but is no longer part of the person's circle.
    return copies(this.db.t.person_portraits.filter((p) => p.personId === personId && !p.removed).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
  }

  async setLocked(personId: UUID, locked: boolean, reason?: string): Promise<CommandResult<PersonWithNames>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const person = this.db.t.persons.find((p) => p.id === personId);
      if (!person) return notFound();
      if (!!person.locked === locked) return okResult(this.withNames(person), { noop: true });
      person.locked = locked;
      if (locked && reason?.trim()) person.lockReason = reason.trim();
      if (!locked && !person.lockedFields.length) person.lockReason = undefined;
      person.version += 1;
      person.updatedAt = nowISO();
      person.updatedBy = actor.session.accountId;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: personId, action: locked ? 'lock' : 'unlock', description: locked ? 'Locked the circle — edits become suggestions' : 'Unlocked the circle', entityVersion: person.version });
      await this.db.commit(['persons', 'audit_events'], [personId]);
      return okResult(this.withNames(person));
    });
  }

  async listStars(): Promise<PersonStar[]> {
    await this.db.read();
    const s = this.db.getSession();
    if (!s) return [];
    return this.db.t.person_stars.filter((x) => x.accountId === s.accountId);
  }

  async setStar(personId: UUID, on: boolean): Promise<CommandResult<{ starred: boolean }>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!this.db.t.persons.some((p) => p.id === personId)) return notFound();
      const existing = this.db.t.person_stars.find((x) => x.accountId === actor.session.accountId && x.personId === personId);
      if (on && !existing) this.db.t.person_stars.push({ id: uuid(), accountId: actor.session.accountId, personId, createdAt: nowISO() });
      if (!on && existing) this.db.t.person_stars = this.db.t.person_stars.filter((x) => x !== existing);
      await this.db.commit(['person_stars']);
      return okResult({ starred: on });
    });
  }

  async listAllPortraits(): Promise<PersonPortrait[]> {
    await this.db.read();
    return copies(this.db.t.person_portraits.filter((p) => !p.removed));
  }

  // -------------------------------------------------------------------------
  // Recognising a newly added person
  //
  // Anyone may add a relative, and until the family recognises them their
  // circle is drawn dimmed rather than hidden — the tree stays whole while the
  // question is open. Enough relatives vouching, or one administrator, settles
  // it (see domain/types.ts → Governance.memberApprovalsRequired).
  // -------------------------------------------------------------------------

  async listApprovals(): Promise<PersonApproval[]> {
    await this.db.read();
    return [...this.db.t.person_approvals];
  }

  /** How the vouching stands for one person, from the current account's point of view. */
  private approvalState(personId: UUID, accountId: UUID | null): PersonApprovalState {
    const approvals = this.db.t.person_approvals.filter((a) => a.personId === personId);
    const person = this.db.t.persons.find((p) => p.id === personId);
    return {
      personId,
      approvals,
      needed: this.db.governance().memberApprovalsRequired,
      approved: person?.reviewStatus === 'approved',
      mine: !!accountId && approvals.some((a) => a.accountId === accountId),
    };
  }

  /** Everything that follows from a person becoming recognised, in one place. */
  private markRecognised(personId: UUID, byAccountId: UUID, how: 'family' | 'admin', reason?: string): void {
    const person = this.db.t.persons.find((p) => p.id === personId);
    if (!person || person.reviewStatus === 'approved') return;
    person.reviewStatus = 'approved';
    person.version += 1;
    person.updatedAt = nowISO();
    person.updatedBy = byAccountId;
    // The links that brought them into the tree become real at the same moment.
    for (const r of this.db.t.relationships) {
      if (r.status !== 'pending') continue;
      if (r.fromPersonId !== personId && r.toPersonId !== personId) continue;
      r.status = 'approved';
      r.updatedAt = nowISO();
      r.updatedBy = byAccountId;
    }
    for (const cr of this.db.t.change_requests) {
      if (cr.status === 'pending' && cr.kind === 'relationship_add' && (cr.payload as { personId?: UUID }).personId === personId) {
        cr.status = 'approved';
        cr.reviewerAccountId = byAccountId;
        cr.updatedAt = nowISO();
      }
    }
    const named = this.db.personWithNames(personId);
    this.db.audit({
      actorAccountId: byAccountId,
      actorRole: how === 'admin' ? 'admin' : undefined,
      entityType: 'person',
      entityId: personId,
      action: 'approve',
      reason: reason?.trim() || undefined,
      description: how === 'admin'
        ? `Recognised ${named ? displayNameOf(named, 'en') : 'a new relative'} as a member of the family`
        : `${named ? displayNameOf(named, 'en') : 'A new relative'} was recognised by the family`,
      entityVersion: person.version,
    });
  }

  async vouchForPerson(personId: UUID, on: boolean): Promise<CommandResult<PersonApprovalState>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const person = this.db.t.persons.find((p) => p.id === personId);
      if (!person) return notFound();
      const account = actor.session.accountId;
      const existing = this.db.t.person_approvals.find((a) => a.personId === personId && a.accountId === account);
      if (on && !existing) {
        this.db.t.person_approvals.push({ id: uuid(), familyId: actor.session.familyId, personId, accountId: account, byAdmin: false, createdAt: nowISO() });
      } else if (!on && existing) {
        this.db.t.person_approvals = this.db.t.person_approvals.filter((a) => a.id !== existing.id);
      }
      const count = this.db.t.person_approvals.filter((a) => a.personId === personId).length;
      if (person.reviewStatus !== 'approved' && count >= this.db.governance().memberApprovalsRequired) {
        this.markRecognised(personId, account, 'family');
      }
      await this.db.commit(['person_approvals', 'persons', 'relationships', 'change_requests', 'audit_events'], [personId]);
      return okResult(this.approvalState(personId, account));
    });
  }

  /**
   * Administrator: hand a circle to a different caretaker — or leave it to
   * nobody by passing null.
   *
   * A circle its own person has claimed is refused: the whole point of claiming
   * your circle is that it stops being anyone else's to give away.
   */
  async setCircleOwner(personId: UUID, accountId: UUID | null, reason?: string): Promise<CommandResult<PersonApprovalState | { ownerAccountId: UUID | null }>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const person = this.db.t.persons.find((p) => p.id === personId);
      if (!person) return notFound();
      if (!canReassignCircle(this.db.t.person_account_links, personId)) return invalid('owner.error.claimed');
      if (accountId && !this.db.membership(accountId)) return notFound();

      const before = circleOwner(this.db.t.person_account_links, this.db.t.profile_management_grants, personId);
      if (before?.accountId === accountId) return okResult({ ownerAccountId: accountId }, { noop: true });
      const now = nowISO();
      // One caretaker at a time: the previous grant is revoked, never deleted.
      for (const g of this.db.t.profile_management_grants) {
        if (g.managedPersonId === personId && g.status === 'active' && g.scope === 'full') g.status = 'revoked';
      }
      if (accountId) {
        this.db.t.profile_management_grants.push({
          id: uuid(), familyId: actor.session.familyId, helperAccountId: accountId, managedPersonId: personId,
          scope: 'full', grantedByAccountId: actor.session.accountId, status: 'active', createdAt: now,
        });
      }
      const named = this.db.personWithNames(personId);
      const who = named ? displayNameOf(named, 'en') : 'a circle';
      this.db.audit({
        actorAccountId: actor.session.accountId,
        actorRole: 'admin',
        entityType: 'grant',
        entityId: personId,
        action: accountId ? 'grant' : 'revoke',
        reason: reason?.trim() || undefined,
        before: { owner: before ? this.db.accountName(before.accountId) : null },
        after: { owner: accountId ? this.db.accountName(accountId) : null },
        description: accountId
          ? `${this.db.accountName(accountId)} now looks after ${who}`
          : `${who} is no longer looked after by anyone`,
      });
      if (accountId && accountId !== actor.session.accountId) {
        this.db.notify({ accountId, kind: 'approval', title: `You now look after ${who}`, titleHe: `את/ה עכשיו אחראי/ת על ${who}` });
      }
      await this.db.commit(['profile_management_grants', 'audit_events', 'notifications'], [personId]);
      return okResult({ ownerAccountId: accountId });
    });
  }

  async approvePersonNow(personId: UUID, reason?: string): Promise<CommandResult<PersonWithNames>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const person = this.db.t.persons.find((p) => p.id === personId);
      if (!person) return notFound();
      if (person.reviewStatus === 'approved') return okResult(this.withNames(person), { noop: true });
      const account = actor.session.accountId;
      if (!this.db.t.person_approvals.some((a) => a.personId === personId && a.accountId === account)) {
        this.db.t.person_approvals.push({ id: uuid(), familyId: actor.session.familyId, personId, accountId: account, byAdmin: true, createdAt: nowISO() });
      }
      this.markRecognised(personId, account, 'admin', reason);
      await this.db.commit(['person_approvals', 'persons', 'relationships', 'change_requests', 'audit_events'], [personId]);
      return okResult(this.withNames(person));
    });
  }

  async getPortraitUrl(portraitId: UUID): Promise<string | undefined> {
    return this.portraitUrls.resolve(portraitId, async () => {
      await this.db.ready;
      const portrait = byId(this.db.t.person_portraits).get(portraitId);
      if (!portrait) return undefined;
      const blob = await getBlob(portrait.storageKey);
      return blob ? URL.createObjectURL(blob) : undefined;
    });
  }

  async getPortraitThumbUrl(portraitId: UUID): Promise<string | undefined> {
    await this.db.ready;
    const portrait = byId(this.db.t.person_portraits).get(portraitId);
    if (!portrait) return undefined;
    const url = await this.portraitThumbUrls.resolve(portraitId, async () => {
      const small = await smallCopy(portraitThumbKey(portraitId), portrait.storageKey, PORTRAIT_THUMB_SIDE);
      return small ? URL.createObjectURL(small) : undefined;
    });
    // A face that could not be shrunk is shown as it is rather than as initials.
    return url ?? this.getPortraitUrl(portraitId);
  }

  /**
   * A face that would not load: forget the url and let the browser release it.
   * The photograph itself is untouched — the next ask reads it again. See the
   * long note on `PhotoRepository.forgetUrls`.
   */
  forgetPortraitUrl(portraitId: UUID): void {
    this.portraitUrls.forget(portraitId);
    this.portraitThumbUrls.forget(portraitId);
  }

  async listLinks(): Promise<PersonAccountLink[]> {
    await this.db.read();
    return [...this.db.t.person_account_links];
  }

  async grantHelper(helperAccountId: UUID, managedPersonId: UUID, scope: ProfileManagementGrant['scope']): Promise<CommandResult<ProfileManagementGrant>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isEditor(actor.session.role) && actor.session.personId !== managedPersonId) return denied();
      const existing = this.db.t.profile_management_grants.find((g) => g.helperAccountId === helperAccountId && g.managedPersonId === managedPersonId && g.status === 'active');
      if (existing) return okResult(existing, { noop: true });
      const g: ProfileManagementGrant = { id: uuid(), familyId: actor.session.familyId, helperAccountId, managedPersonId, scope, grantedByAccountId: actor.session.accountId, status: 'active', createdAt: nowISO() };
      this.db.t.profile_management_grants.push(g);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'grant', entityId: g.id, action: 'grant', description: `${this.db.accountName(helperAccountId)} may manage a person's profile (${scope})` });
      this.db.notify({ accountId: helperAccountId, kind: 'approval', title: 'You can now help manage a family member’s profile', titleHe: 'עכשיו אפשר לעזור בניהול פרופיל של בן/בת משפחה', link: '/me' });
      await this.db.commit(['profile_management_grants', 'audit_events', 'notifications']);
      return okResult(g);
    });
  }

  async revokeGrant(grantId: UUID): Promise<CommandResult<{ revoked: true }>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const g = this.db.t.profile_management_grants.find((x) => x.id === grantId);
      if (!g) return notFound();
      if (!isEditor(actor.session.role) && actor.session.personId !== g.managedPersonId) return denied();
      g.status = 'revoked';
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'grant', entityId: g.id, action: 'revoke', description: 'Profile helper access revoked' });
      await this.db.commit(['profile_management_grants', 'audit_events']);
      return okResult({ revoked: true as const });
    });
  }

  async history(personId: UUID): Promise<AuditEvent[]> {
    await this.db.read();
    return this.db.t.audit_events.filter((a) => a.entityId === personId).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }

  async lockFields(personId: UUID, fields: string[], reason: string): Promise<CommandResult<PersonWithNames>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const p = this.db.t.persons.find((x) => x.id === personId);
      if (!p) return notFound();
      p.lockedFields = [...new Set([...p.lockedFields, ...fields])];
      p.lockReason = reason;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: p.id, action: 'lock', description: `Locked ${fields.join(', ')}: ${reason}` });
      await this.db.commit(['persons', 'audit_events'], [p.id]);
      return okResult(this.withNames(p));
    });
  }

  async unlockFields(personId: UUID): Promise<CommandResult<PersonWithNames>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const p = this.db.t.persons.find((x) => x.id === personId);
      if (!p) return notFound();
      p.lockedFields = [];
      // The reason belongs to whichever lock is left: the whole circle may still be locked (see setLocked).
      if (!p.locked) p.lockReason = undefined;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: p.id, action: 'unlock', description: 'Unlocked all fields' });
      await this.db.commit(['persons', 'audit_events'], [p.id]);
      return okResult(this.withNames(p));
    });
  }

  async archive(personId: UUID): Promise<CommandResult<{ archived: true }>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const p = this.db.t.persons.find((x) => x.id === personId);
      if (!p) return notFound();
      p.archived = true;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: p.id, action: 'archive', description: 'Person archived (recoverable)' });
      await this.db.commit(['persons', 'audit_events'], [p.id]);
      return okResult({ archived: true as const });
    });
  }

  async restore(personId: UUID): Promise<CommandResult<PersonWithNames>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const p = this.db.t.persons.find((x) => x.id === personId);
      if (!p) return notFound();
      p.archived = false;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: p.id, action: 'restore', description: 'Person restored' });
      await this.db.commit(['persons', 'audit_events'], [p.id]);
      return okResult(this.withNames(p));
    });
  }

  /** Used by the sync repository when replaying offline operations. */
  async replay(cmd: UpdatePersonCommand): Promise<CommandResult<PersonWithNames>> {
    try {
      await this.db.write();
    } catch (e) {
      if (e instanceof NetworkError) return { ok: false, kind: 'network', messageKey: 'error.offline', retryable: true };
    }
    return this.applyUpdate(cmd, false);
  }

  canDirect(personId: UUID): boolean {
    try {
      return canEditPersonDirectly(actorOf(this.db), personId);
    } catch {
      return false;
    }
  }

  manages(personId: UUID): boolean {
    try {
      return managesPerson(actorOf(this.db), personId);
    } catch {
      return false;
    }
  }
}

function stripIds(n: PersonName): Omit<PersonName, 'id' | 'personId'> {
  const { id, personId, ...rest } = n;
  void id;
  void personId;
  return rest;
}
